import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import Sidebar from '../../components/Sidebar/Sidebar';
import PhasePlan from '../../components/PhasePlan/PhasePlan';
import Deliverables from '../../components/Deliverables/Deliverables';
import CheckPlan from '../../components/CheckPlan/CheckPlan';
import Tasks from '../../components/Tasks/Tasks';
import ActionPoints from '../../components/ActionPoints/ActionPoints';
import ReportMetrics from '../../components/ReportMetrics/ReportMetrics';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import NotificationBell from '../../components/NotificationBell/NotificationBell';
import SystemAdminBadge from '../../components/SystemAdminBadge/SystemAdminBadge';
import styles from './ProjectPage.module.scss';
import { inAppClick } from '../../utils/linkClick';

// will be added to styles module later
const additionalStyles = `
.loadingContainer,
.errorContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  padding: 2rem;
}

.errorContainer button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.errorContainer button:hover {
  background-color: #1557b0;
}
`;
import { useGetProjectId, usePutProjectCharacter, useDeleteProjectCharacter } from '../../api/project-api/project-api';
import { ProjectDto } from '../../api/model';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog/ConfirmationDialog';
import ProjectObjectDialog from '../../components/ProjectObjectDialog/ProjectObjectDialog';
import { objectFromSearch, objectSearch, type ObjectTarget } from '../../components/ReportMetrics/detail';
import { useNotifications } from '../../hooks/useNotifications';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';

import {
  IconUsersGroup,
  IconUser,
  IconChevronDown,
  IconChevronUp,
  IconSettings,
  IconTimeline,
  IconArchive,
  IconTemplate,
  IconPrinter,
  IconRocket,
  IconArrowLeft,
  IconHome,
  IconUsers,
  IconPlus,
  IconX,
  IconCircleDashedLetterC
} from '@tabler/icons-react';

interface TeamMember {
  id: string;
  name: string;
  avatar: string;
}

// values
const SECTION_ID_MAP: Record<string, string> = {
  'Cockpit': 'phase-plan-v2',
  'Phase Plan V2': 'phase-plan-v2',
  'Deliverables': 'deliverables',
  'Check Plan': 'check-plan',
  'Prüfplan': 'check-plan',
  'Tasks': 'tasks',
  'Aufgaben': 'tasks',
  'Action Points': 'task-action-points',
  'Task / Action Points': 'task-action-points',
  'Aufgaben / Maßnahmen': 'task-action-points',
  'Maßnahmen': 'task-action-points',
  'Aufgaben / Massnahmen': 'task-action-points',
  'Massnahmen': 'task-action-points',
  'Report / Metrics': 'report-metrics',
  'Berichte / Metriken': 'report-metrics',
  'Lieferobjekte': 'deliverables',
};
export const VALID_SECTION_IDS = new Set(['phase-plan-v2', 'deliverables', 'check-plan', 'tasks', 'task-action-points', 'report-metrics']);
const migrateSectionId = (value: string | null): string | null => {
  if (!value) return null;
  if (VALID_SECTION_IDS.has(value)) return value;
  return SECTION_ID_MAP[value] || null;
};

const ProjectPage: React.FC = () => {
  const { t, i18n } = useTranslation('project');
  const { isSystemAdmin } = useCurrentUserPermissions();
  const { projectId } = useParams({ from: '/project/$projectId' });
  const {
    section: sectionFromUrl,
    taskId: taskIdFromUrl,
    deliverableId: deliverableIdFromUrl,
    checkpointId: checkpointIdFromUrl,
    actionPointId: actionPointIdFromUrl,
  } = useSearch({ from: '/project/$projectId' });
  const navigate = useNavigate();
  const [isSideMenuCollapsed, setIsSideMenuCollapsed] = useState(false);
  const { notifySuccess, notifyError } = useNotifications();
  
  const numericProjectId = Number(projectId);
  const isValidProjectId = !isNaN(numericProjectId) && numericProjectId > 0;
  
  const { data: projectData, isLoading, isError, refetch } = useGetProjectId(
    numericProjectId,
    { 
      query: { 
        retry: 1,
        enabled: isValidProjectId 
      } 
    }
  );

  const addCharacterMutation = usePutProjectCharacter({
    mutation: {
      onSuccess: () => {
        refetch();
        notifySuccess(t('header.characters.toast.addedTitle'), t('header.characters.toast.addedMessage', { name: newCharacterName.trim() }));
        setNewCharacterName('');
        setShowAddConfirmation(false);
      },
      onError: (error) => {
        console.error('Failed:', error);
        notifyError(t('header.characters.toast.addFailedTitle'), error);
      }
    }
  });

  const deleteCharacterMutation = useDeleteProjectCharacter({
    mutation: {
      onSuccess: () => {
        refetch();
        notifySuccess(t('header.characters.toast.removedTitle'), t('header.characters.toast.removedMessage', { name: characterToDelete }));
        setShowDeleteConfirmation(false);
        setCharacterToDelete('');
      },
      onError: (error) => {
        console.error('Failed:', error);
        notifyError(t('header.characters.toast.removeFailedTitle'), error);
      }
    }
  });

  const [currentProject, setCurrentProject] = useState({
    id: projectId,
    title: 'Loading...',
    members: [] as TeamMember[]
  });
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // The page shown is the URL's `section`, so links, reloads and the browser's
  // back button all agree on it; without one, the project's last page is used.
  const savedSection = migrateSectionId(localStorage.getItem(`projectActiveSection_${projectId}`)) || 'phase-plan-v2';
  const activeSection = sectionFromUrl ?? savedSection;
  // A task, deliverable, checkpoint or action point named in the URL is shown as
  // a dialog over the current page, which stays as it is underneath.
  const openObject = objectFromSearch({
    taskId: taskIdFromUrl,
    deliverableId: deliverableIdFromUrl,
    checkpointId: checkpointIdFromUrl,
    actionPointId: actionPointIdFromUrl,
  });
  
  const [isCharactersWidgetOpen, setIsCharactersWidgetOpen] = useState(false);
  const [newCharacterName, setNewCharacterName] = useState('');
  const [showAddConfirmation, setShowAddConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState<string>('');

  // handling invlalid project ID requested.
  if (!isValidProjectId) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>Invalid Project ID</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          The project ID "{projectId}" is not valid. Please check the URL and try again.
        </p>
        <button 
          onClick={() => navigate({ to: '/dashboard' })}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const MAX_VISIBLE_MEMBERS = 3;
  const currentDate = new Date().toLocaleDateString(
    i18n.language?.toLowerCase().startsWith('de') ? 'de-DE' : 'en-US',
    {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
  );
  
  useEffect(() => {
    if (projectData) {
      setCurrentProject({
        id: projectId,
        title: projectData.name || 'Untitled Project',
        members: []
      });
    }
  }, [projectData, projectId]);

    // Every page of the project has its section in the URL, the first one too,
    // so going back to it with the browser lands on the page it showed.
    useEffect(() => {
        if (sectionFromUrl) return;
        void navigate({
            to: '/project/$projectId',
            params: { projectId },
            search: (previous) => ({ ...previous, section: savedSection }),
            replace: true,
        });
    }, [sectionFromUrl, savedSection, navigate, projectId]);

    // Opens an object's detail over the current page, or closes it (undefined).
    // A plain function, not useCallback: this component already calls hooks after
    // an early return, and adding another would widen that problem.
    const showObject = (target?: ObjectTarget) => {
        void navigate({
            to: '/project/$projectId',
            params: { projectId },
            search: (previous) => ({ ...previous, ...objectSearch(target) }),
            replace: true,
        });
    };

  // Save state sidemenuitem
  useEffect(() => {
    localStorage.setItem(`projectActiveSection_${projectId}`, activeSection);
  }, [activeSection, projectId]);

  // Close characters widget if user click outside of box.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isCharactersWidgetOpen && !target.closest(`.${styles.charactersButtonWrapper}`)) {
        setIsCharactersWidgetOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCharactersWidgetOpen]);

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    navigate({ to: '/dashboard' });
  };
  
  if (isLoading) {
    return <div className={styles.loadingContainer}>Loading project data...</div>;
  }

  if (isError) {
    return (
      <div className={styles.errorContainer}>
        <p>Error loading project. Please try again.</p>
        <button onClick={() => navigate({ to: '/dashboard' })}>Return to Dashboard</button>
      </div>
    );
  }

  // Handle side menu collapse
  const handleSideMenuCollapse = () => {
    setIsSideMenuCollapsed(!isSideMenuCollapsed);
  };

  // Handle section change from sidebar to display the corresponding content
  const handleSectionChange = (sectionName: string) => {
    void navigate({
      to: '/project/$projectId',
      params: { projectId },
      search: (previous) => ({ ...previous, section: sectionName, ...objectSearch(undefined) }),
    });
  };

  // A deliverable's task opens over the page, like any other object
  const handleNavigateToTask = (taskId: number) => showObject({ kind: 'task', id: taskId });

  // Handle project characters
  const handleCharactersWidgetToggle = () => {
    setIsCharactersWidgetOpen(!isCharactersWidgetOpen);
  };

  const handleAddCharacter = () => {
    const trimmedName = newCharacterName.trim();
    if (trimmedName && !projectData?.projectCharacters?.includes(trimmedName)) {
      setShowAddConfirmation(true);
    }
  };

  const handleConfirmAddCharacter = () => {
    const trimmedName = newCharacterName.trim();
    if (trimmedName) {
      addCharacterMutation.mutate({
        params: { 
          id: numericProjectId,
          character: trimmedName
        }
      });
    }
  };

  const handleRemoveCharacter = (characterName: string) => {
    setCharacterToDelete(characterName);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDeleteCharacter = () => {
    if (characterToDelete) {
      deleteCharacterMutation.mutate({
        params: { 
          id: numericProjectId,
          character: characterToDelete 
        }
      });
    }
  };

  const handleNewCharacterKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddCharacter();
    }
  };

  // Render active section content switch case logic..
  const renderActiveSection = () => {
    switch (activeSection) {
      case 'phase-plan-v2':
      case 'Cockpit':
      case 'Phase Plan V2':
        return <PhasePlan project={projectData} onProjectUpdate={refetch} />;
      case 'deliverables':
      case 'Deliverables':
        return (
          <Deliverables
            project={projectData}
            onNavigateToTask={handleNavigateToTask}
          />
        );
      case 'check-plan':
      case 'Check Plan':
        return (
          <CheckPlan project={projectData} />
        );
      case 'tasks':
      case 'Tasks':
        return <Tasks project={projectData} onDataRefresh={refetch} />;
      case 'task-action-points':
      case 'Action Points':
      case 'Task / Action Points':
        return (
          <ActionPoints project={projectData} onDataRefresh={refetch} />
        );
      case 'report-metrics':
      case 'Report / Metrics':
        return <ReportMetrics project={projectData} />;
      default:
        return <PhasePlan project={projectData} onProjectUpdate={refetch} />;
    }
  };
  // Show error only if API fails and we have no project data
  if (isError && !projectData) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>Project Not Found</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          The project with ID "{projectId}" could not be found. It may have been deleted or you may not have access to it.
        </p>
        <button 
          onClick={() => navigate({ to: '/dashboard' })}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {projectData && (
        <Sidebar 
          project={projectData}
          onBackToDashboard={handleBackToDashboard}
          onCollapse={handleSideMenuCollapse}
          isCollapsed={isSideMenuCollapsed}
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          useRouter={false}
        />
      )}
      <div className={styles.content}>
        <div className={styles.contentHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.projectTitle}>
              {projectData?.name || t('header.unknownProject')}
            </h1>
            <span className={styles.projectDate}>{currentDate}</span>
            <div className={styles.teamSection}>
              <div className={styles.teamMembers}>
              </div>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <LanguageSwitcher />
            {isSystemAdmin ? <SystemAdminBadge /> : <NotificationBell />}
            <button
              className={styles.homeButton}
              onClick={handleBackToDashboard}
              aria-label={t('header.backToDashboard')}
            >
              <IconHome />
            </button>
            <div className={styles.charactersButtonWrapper}>
              <button
                className={`${styles.button} ${isCharactersWidgetOpen ? styles.charactersButtonActive : ''}`}
                onClick={handleCharactersWidgetToggle}
                aria-label={t('header.characters.button')}
                title={t('header.characters.button')}
              >
                <IconCircleDashedLetterC />
                <span className={styles.buttonText}>{t('header.characters.button')}</span>
              </button>
              {isCharactersWidgetOpen && (
                <div className={styles.charactersWidget}>
                  <div className={styles.charactersWidgetHeader}>
                    <h3 className={styles.charactersWidgetTitle}>
                      {t('header.characters.widgetTitle', { count: projectData?.projectCharacters?.length || 0 })}
                    </h3>
                    <button
                      className={styles.charactersWidgetClose}
                      onClick={() => setIsCharactersWidgetOpen(false)}
                      aria-label={t('header.characters.close')}
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                  <div className={styles.charactersWidgetContent}>
                    {(addCharacterMutation.isPending || deleteCharacterMutation.isPending) && (
                      <div className={styles.loadingOverlay}>
                        <div className={styles.loadingSpinner}></div>
                        <p>
                          {addCharacterMutation.isPending ? t('header.characters.addingCharacter') : t('header.characters.removingCharacter')}
                        </p>
                      </div>
                    )}
                    <div className={styles.addCharacterSection}>
                      <div className={styles.inputWrapper}>
                        <input
                          type="text"
                          value={newCharacterName}
                          onChange={(e) => setNewCharacterName(e.target.value)}
                          onKeyPress={handleNewCharacterKeyPress}
                          placeholder={t('header.characters.namePlaceholder')}
                          maxLength={55}
                          className={`${styles.characterInput} ${
                            newCharacterName.trim() && projectData?.projectCharacters?.includes(newCharacterName.trim())
                              ? styles.duplicate
                              : ''
                          }`}
                        />
                        <span className={styles.characterCounter}>
                          {newCharacterName.length}/55
                        </span>
                      </div>
                      <button
                        className={styles.addCharacterButton}
                        onClick={handleAddCharacter}
                        disabled={!newCharacterName.trim() || projectData?.projectCharacters?.includes(newCharacterName.trim()) || addCharacterMutation.isPending}
                        title={
                          !newCharacterName.trim() ? t('header.characters.enterName') :
                          projectData?.projectCharacters?.includes(newCharacterName.trim()) ? t('header.characters.alreadyExists') :
                          addCharacterMutation.isPending ? t('header.characters.addingCharacter') :
                          t('header.characters.addCharacter')
                        }
                      >
                        <IconPlus size={16} />
                      </button>
                    </div>
                    <div className={styles.charactersList}>
                      {!projectData?.projectCharacters || projectData.projectCharacters.length === 0 ? (
                        <div className={styles.noCharacters}>
                          <IconUsers size={32} className={styles.noCharactersIcon} />
                          <p>{t('header.characters.noCharacters')}</p>
                        </div>
                      ) : (
                        projectData.projectCharacters.map((character: string, index: number) => (
                          <div key={index} className={styles.characterItem}>
                            <span className={styles.characterName} title={character}>{character}</span>
                            <button
                              className={styles.removeCharacterButton}
                              onClick={() => handleRemoveCharacter(character)}
                              aria-label={t('header.characters.removeCharacter', { name: character })}
                              title={t('header.characters.removeCharacter', { name: character })}
                              disabled={deleteCharacterMutation.isPending}
                            >
                              <IconX size={14} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <a
              className={styles.button}
              href={`/project/${projectId}/settings`}
              onClick={inAppClick(() => navigate({ to: '/project/$projectId/settings', params: { projectId } }))}
            >
              <IconSettings />
              <span className={styles.buttonText}>{t('header.settingsButton')}</span>
            </a>
            <button className={styles.button} style={{ display: 'none' }}>
              <IconUsersGroup />
              <span className={styles.buttonText}>Manage Team Members</span>
            </button>
            <div className={styles.userMenuWrapper} style={{ display: 'none' }}>
              <button
                className={isUserMenuOpen ? `${styles.button} ${styles.userMenuButtonOpen}` : styles.button}
                onClick={() => setIsUserMenuOpen((open) => !open)}
              >
                <IconUser className={isUserMenuOpen ? styles.userMenuIconOpen : styles.userMenuIcon} />
                <span className={isUserMenuOpen ? `${styles.buttonText} ${styles.userMenuTextOpen}` : styles.buttonText}>
                  User Menu
                </span>
                {isUserMenuOpen ? (
                  <IconChevronUp className={styles.userMenuIconOpen} />
                ) : (
                  <span className={styles.userMenuIcon}>
                    <IconChevronDown />
                  </span>
                )}
              </button>
              {isUserMenuOpen && (
                <div className={styles.userMenuDropdown}>
                  <div className={styles.userMenuList}>
                    <div className={styles.userMenuItem}>
                      <IconSettings className={styles.userMenuIcon} />
                      <span>Information</span>
                    </div>
                    <div className={styles.userMenuItem}>
                      <IconTimeline className={styles.userMenuIcon} />
                      <span>Activities</span>
                    </div>
                    <div className={styles.userMenuItem}>
                      <IconArchive className={styles.userMenuIcon} />
                      <span>Archive</span>
                    </div>
                    <div className={styles.userMenuItem}>
                      <IconSettings className={styles.userMenuIcon} />
                      <span>Settings</span>
                    </div>
                    <div className={styles.userMenuItem}>
                      <IconTemplate className={styles.userMenuIcon} />
                      <span>Create Templates</span>
                    </div>
                    <div className={styles.userMenuItem}>
                      <IconRocket className={styles.userMenuIcon} />
                      <span>Deploy</span>
                    </div>
                    <div className={styles.userMenuItem}>
                      <IconPrinter className={styles.userMenuIcon} />
                      <span>Print</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {renderActiveSection()}
      </div>
      <ProjectObjectDialog
        project={projectData}
        target={openObject}
        onClose={() => showObject(undefined)}
        onOpen={showObject}
      />
      <ConfirmationDialog
        isOpen={showAddConfirmation}
        onClose={() => setShowAddConfirmation(false)}
        onConfirm={handleConfirmAddCharacter}
        title="Add Character"
        message={`Are you sure you want to add "${newCharacterName.trim()}" to the project characters?`}
        confirmText="Add"
        cancelText="Cancel"
        type="info"
      />
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDeleteCharacter}
        title="Remove Character"
        message={`Are you sure you want to remove "${characterToDelete}" from the project characters?`}
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default ProjectPage;